/**
 * DealDescriptionAndAttachments - Componente unificado de descrição e anexos
 * 
 * Combina editor de texto rico e gerenciamento de anexos em um único card
 * com fundo diferenciado para destaque visual.
 * 
 * Versão: 1.1.0
 * RELEASE: 2026-04-06 v1.1.0 - Refatoração para usar componentes compartilhados
 */
import React, { useState, useCallback, useRef } from 'react';
import { ImageLightbox, MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { canPreviewInline, resolvePreviewMediaType, downloadAttachmentFile } from '@/lib/attachment-preview';
import { 
  Menu, 
  Pencil, 
  X, 
  Save, 
  Paperclip, 
  Loader2,
  AArrowUp
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useDealAttachments, DealAttachment } from '@/hooks/useDealAttachments';
import { sanitizeHtml } from '@/lib/sanitize';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getFileIcon, getFileColor, formatFileSize } from '@/lib/file-utils';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';

// Props do componente
interface DealDescriptionAndAttachmentsProps {
  dealId: string;
  content?: string | null;
  plainContent?: string | null;
  readOnly?: boolean;
  onSaveDescription: (html: string) => Promise<void>;
  className?: string;
}

/**
 * Componente de item de anexo para modo edição (exclusivo do CRM)
 */
function AttachmentListItem({ 
  attachment, 
  onDelete,
  isDeleting
}: { 
  attachment: DealAttachment; 
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const FileIcon = getFileIcon(attachment.file_type);
  const colorClasses = getFileColor(attachment.file_type);

  return (
    <div className="flex items-center justify-between p-2 bg-background rounded border">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className={cn('p-1.5 rounded', colorClasses)}>
          <FileIcon className="h-4 w-4" />
        </div>
        <span className="text-sm truncate">{attachment.file_name}</span>
      </div>
      <div className="flex items-center gap-2 ml-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatFileSize(attachment.file_size)}
        </span>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6"
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3 text-destructive" />
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Componente principal - Descrição e Anexos unificados
 */
export function DealDescriptionAndAttachments({
  dealId,
  content,
  plainContent,
  readOnly = false,
  onSaveDescription,
  className
}: DealDescriptionAndAttachmentsProps) {
  // Estados de edição
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Estado do tamanho de fonte persistido no localStorage
  const [fontSize, setFontSize] = useState<'xs' | 'sm' | 'base'>(() => {
    const saved = localStorage.getItem('deal-description-font-size');
    return (saved === 'xs' || saved === 'sm' || saved === 'base') ? saved : 'xs';
  });

  /** Atualiza tamanho de fonte e persiste no localStorage */
  const handleFontSizeChange = useCallback((size: 'xs' | 'sm' | 'base') => {
    setFontSize(size);
    localStorage.setItem('deal-description-font-size', size);
  }, []);
  
  // Estados para o lightbox de visualização de imagens
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMediaItems, setLightboxMediaItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Referência para input de arquivo
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hook de anexos
  const { 
    attachments, 
    isLoading: isLoadingAttachments, 
    uploadFile, 
    deleteAttachment,
    isUploading 
  } = useDealAttachments(dealId);

  // Conteúdo atual para exibição
  const displayContent = content || plainContent || '';
  const hasContent = Boolean(displayContent.trim());
  const attachmentCount = attachments?.length || 0;

  // Iniciar edição - considera plainContent como fallback para o editor
  const handleStartEdit = useCallback(() => {
    setEditedContent(content || (plainContent ? `<p>${plainContent}</p>` : ''));
    setIsEditing(true);
  }, [content, plainContent]);

  // Cancelar edição
  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditedContent('');
  }, []);

  // Salvar alterações
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      const sanitized = sanitizeHtml(editedContent);
      await onSaveDescription(sanitized);
      setIsEditing(false);
      setEditedContent('');
    } catch (error) {
      // Erro já é tratado pelo componente pai
    } finally {
      setIsSaving(false);
    }
  }, [editedContent, onSaveDescription]);

  // Upload de múltiplos arquivos
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const MAX_SIZE = 10 * 1024 * 1024;

    const uploadPromises = Array.from(files).map(file => {
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name}: Arquivo muito grande (máx 10MB)`);
        return Promise.resolve();
      }
      return uploadFile(file).catch(error => {
        console.error(`Erro ao enviar ${file.name}:`, error);
      });
    });
    
    await Promise.allSettled(uploadPromises);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadFile]);

  // Deletar anexo
  const handleDeleteAttachment = useCallback(async (attachmentId: string) => {
    setDeletingId(attachmentId);
    try {
      await deleteAttachment(attachmentId);
    } finally {
      setDeletingId(null);
    }
  }, [deleteAttachment]);

  // Trigger do input de arquivo
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handler para clique em anexo - abre lightbox para imagens ou nova aba para outros
  // Abre PREVIEW INTERNO (imagem/vídeo/PDF); demais formatos são baixados
  const handleAttachmentClick = useCallback(async (attachment: DealAttachment) => {
    if (canPreviewInline(attachment.file_type, (attachment as any).mime_type)) {
      const viewableAttachments =
        attachments?.filter(a => canPreviewInline(a.file_type, (a as any).mime_type)) || [];

      const mediaItems: MediaItem[] = viewableAttachments.map(a => ({
        url: a.public_url || a.thumbnail_url || '',
        type: resolvePreviewMediaType(a.file_type, (a as any).mime_type) ?? 'document',
        title: a.file_name,
      }));

      const viewableIndex = viewableAttachments.findIndex(a => a.id === attachment.id);

      setLightboxMediaItems(mediaItems);
      setLightboxIndex(viewableIndex >= 0 ? viewableIndex : 0);
      setLightboxOpen(true);
      return;
    }
    try {
      await downloadAttachmentFile(attachment.public_url, attachment.file_name);
    } catch {
      toast.error('Erro ao baixar arquivo');
    }
  }, [attachments]);

  return (
    <div className={cn('rounded-lg border bg-accent/30', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <Menu className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">
            {isEditing ? 'Editando Descrição e Anexos' : 'Descrição'}
          </h4>
        </div>
        
        {isEditing ? (
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7"
              onClick={handleCancel}
              disabled={isSaving}
              title="Cancelar"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            <Button 
              size="icon"
              className="h-7 w-7"
              onClick={handleSave}
              disabled={isSaving}
              title="Salvar"
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-0.5">
            {/* Seletor de tamanho de fonte da descrição */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7"
                  title="Tamanho da fonte"
                >
                  <AArrowUp className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-36 p-1">
                {([
                  { value: 'xs' as const, label: 'Pequena' },
                  { value: 'sm' as const, label: 'Média' },
                  { value: 'base' as const, label: 'Grande' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleFontSizeChange(opt.value)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors',
                      'hover:bg-accent',
                      fontSize === opt.value && 'bg-accent font-medium'
                    )}
                  >
                    <span className={cn(
                      'text-muted-foreground',
                      opt.value === 'xs' && 'text-xs',
                      opt.value === 'sm' && 'text-sm',
                      opt.value === 'base' && 'text-base',
                    )}>Aa</span>
                    <span className="text-xs">{opt.label}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>

            {/* Botão de edição */}
            {!readOnly && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={handleStartEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Conteúdo */}
      {isEditing ? (
        <>
          {/* Editor de texto rico */}
          <div className="p-3">
            <RichTextEditor
              content={editedContent}
              onChange={setEditedContent}
              placeholder="Adicione uma descrição detalhada do negócio..."
              minHeight="120px"
            />
          </div>

          {/* Área de anexos em modo edição */}
          <div className="p-3 border-t">
            <div className="flex items-center gap-2 mb-3">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Anexos</span>
              {attachmentCount > 0 && (
                <span className="text-xs text-muted-foreground">({attachmentCount})</span>
              )}
            </div>

            {/* Área de upload compartilhada */}
            <AttachmentUploadArea
              onUploadClick={handleUploadClick}
              isUploading={isUploading}
              maxSizeLabel="Máx: 10MB por arquivo | Múltiplos permitidos"
              className="mb-3"
            />

            {/* Lista de arquivos anexados */}
            {attachmentCount > 0 && (
              <div className="space-y-2">
                {attachments?.map(attachment => (
                  <AttachmentListItem
                    key={attachment.id}
                    attachment={attachment}
                    onDelete={() => handleDeleteAttachment(attachment.id)}
                    isDeleting={deletingId === attachment.id}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Descrição em modo visualização */}
          <div className="p-3">
            {hasContent ? (
              <div className="max-h-[200px] overflow-y-auto scrollbar-thin">
                <RichTextEditor
                  content={displayContent}
                  readOnly
                  showToolbar={false}
                  minHeight="60px"
                  fontSize={fontSize}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Nenhuma descrição adicionada
              </p>
            )}
          </div>

          {/* Separador sutil entre descrição e anexos */}
          <div className="px-3">
            <div className="border-t border-border" />
          </div>

          {/* Anexos em modo visualização - usa componente compartilhado */}
          <div className="p-3 pt-3">
            <div className="flex items-center gap-2 mb-3">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Anexos ({attachmentCount})
              </span>
            </div>

            {isLoadingAttachments ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : attachmentCount > 0 ? (
              <div className="flex flex-wrap gap-2">
                {attachments?.map(attachment => (
                  <AttachmentThumbnail
                    key={attachment.id}
                    attachment={attachment}
                    readOnly={readOnly}
                    onDelete={() => handleDeleteAttachment(attachment.id)}
                    onClick={() => handleAttachmentClick(attachment)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Nenhum anexo
              </p>
            )}
          </div>
        </>
      )}

      {/* Input de arquivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
      />

      {/* Lightbox para visualização de imagens */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxMediaItems}
        initialIndex={lightboxIndex}
      />
    </div>
  );
}
